type Handler = (event: Event) => void
type AttrValue = string | number | boolean | Handler | undefined
export type Child = Node | string | number | null | undefined | false

/**
 * Builds an element. Attribute keys starting with `on` become listeners,
 * booleans toggle attributes, and children are appended as text nodes
 * (never as HTML), so data can't inject markup.
 */
export const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, AttrValue> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag)
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue
    if (typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value)
    } else if (value === true) {
      node.setAttribute(key, '')
    } else {
      node.setAttribute(key, String(value))
    }
  }
  append(node, children)
  return node
}

export const append = (parent: Node, children: Child[]): void => {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue
    parent.appendChild(child instanceof Node ? child : document.createTextNode(String(child)))
  }
}

export const clear = (node: Node): void => {
  while (node.firstChild) node.removeChild(node.firstChild)
}

export const replaceChildren = (node: Node, children: Child[]): void => {
  clear(node)
  append(node, children)
}
